<template>
  <el-dialog
    class="advanced-revision-wizard"
    visible
    :close-on-click-modal="false"
    :show-close="false"
    @close="handleClose"
  >
    <ContentHeader
      slot="title"
      :title="modalTitle"
    />

    <el-row :gutter="20">
      <el-col :span="24">
        <el-steps
          :active="stepIndex"
          finish-status="success"
          :align-center="true"
        >
          <el-step
            v-for="key in steps"
            :key="key"
            :title="$t(`advancedRevision.wizard.steps.${key}`)"
          />
        </el-steps>
      </el-col>
    </el-row>

    <el-divider />

    <ValidationObserver v-slot="{ handleSubmit, invalid }">
      <el-form
        ref="form"
        v-loading="loading"
        label-position="top"
      >
        <RevisionReasonStep
          v-if="currentStepKey === 'reason'"
          :form="form"
          :error-types="errorTypes"
        />
        <RevisionSampleStep
          v-else-if="currentStepKey === 'sample' && labSample"
          :form="form"
          :lab-sample="labSample"
        />
        <RevisionAnalysesStep
          v-else-if="currentStepKey === 'analyses'"
          :analyses="analyses"
          :sample="labSample"
          :locations="locations"
        />
        <RevisionTemperatureStep
          v-else-if="currentStepKey === 'temperature'"
          :trails="trails"
          :analysis-options="temperatureAnalysisOptions"
          :loading.sync="loading"
          @refresh="refreshTemperatureTrails"
        />

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="handleClose"
          >
            {{ $t('global.cancel') }}
          </el-button>
          <el-button
            v-if="stepIndex > 0"
            type="secondary"
            @click.prevent="prevStep"
          >
            {{ $t('global.prev') }}
          </el-button>
          <el-button
            v-if="!isLastStep"
            type="primary"
            :disabled="invalid"
            @click.prevent="nextStep"
          >
            {{ $t('global.next') }}
          </el-button>
          <el-button
            v-else
            type="primary"
            native-type="submit"
            :disabled="invalid"
            :loading="submitting"
            @click.prevent="handleSubmit(submit)"
          >
            {{ $t('global.confirm') }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { advancedRevisionApi, locationsManagementApi } from '@/api';
  import {
    SampledBy,
    SampleReportType,
    SAMPLE_REPORT_STATUSES,
    SymbolTypes,
  } from '@/config/constants';
  import analysisStatusManagementHelper from '@/mixins/AnalysisStatusManagementHelper';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import RevisionReasonStep from './wizard/RevisionReasonStep.vue';
  import RevisionSampleStep from './wizard/RevisionSampleStep.vue';
  import RevisionAnalysesStep from './wizard/RevisionAnalysesStep.vue';
  import RevisionTemperatureStep from './wizard/RevisionTemperatureStep.vue';

  // Allowed reason set depends on the report's signature status
  // the FE surfaces only the valid options.
  //   SIGNED   → own (Errore Complife), client (Richiesta cliente)
  //   UNSIGNED → correction (Correzione pre-firma), other (Altro)
  const SIGNED_ERROR_TYPES = ['own', 'client'];
  const UNSIGNED_ERROR_TYPES = ['correction', 'other'];

  export default {
    name: 'AdvancedRevisionWizard',
    components: {
      ContentHeader,
      FormButtons,
      RevisionReasonStep,
      RevisionSampleStep,
      RevisionAnalysesStep,
      RevisionTemperatureStep,
    },
    props: {
      sample: { type: Object, required: true },
      analysisType: { type: String, default: SampleReportType.Default },
      mode: { type: String, default: null },
    },
    data() {
      return {
        submitting: false,
        loading: false,
        stepIndex: 0,
        locations: [],
        labSample: null,
        analyses: [],
        trails: [],
        // Sticky flag: true once the sample is known to carry temperature
        // trails. Keeps the 'temperature' step in the stepper even after the
        // user deletes them all, so a new one can still be added.
        hasTemperature: false,
        form: {
          errorType: null,
          errorNotes: null,
          internalNotes: null,
          toBeInvoiced: false,
          accredited: false,
          sample: {
            sampleAnalysisStart: null,
            sampleAnalysisStartTouched: false,
            sampleAnalysisEnd: null,
            sampleAnalysisEndTouched: false,
            acceptedAt: null,
            lastReportDate: null,
            sector: null,
            sampleDescription: null,
            packagesNumber: null,
            productCode: null,
            productName: null,
            customerProductName: null,
            lot: null,
            date: null,
            finalCustomer: null,
            dilution: null,
            notes: null,
            comments: null,
            costCenter: null,
            addressId: null,
            customerAddress: null,
            customerHeaderData: null,
            address: null,
            addressCountryPatch: null,
            analysisNotes: null,
            labNotes: null,
            samplingInfo: {
              date: null,
              plan: null,
              place: null,
              method: null,
              sampledBy: SampledBy.Customer,
            },
          },
        },
      };
    },
    computed: {
      labSampleId() {
        return this.sample.labSampleId ?? this.sample.id;
      },
      // Analisi selezionabili per associare una nuova traccia temperatura.
      // Mappa codice analisi -> labSampleAnalysisId, deduplicata, derivata
      // dalle analisi già caricate (lo step temperatura segue quello analisi).
      temperatureAnalysisOptions() {
        const byId = new Map();
        this.analyses.forEach((analysis) => {
          const labSampleAnalysisId =
            analysis.labSampleAnalysisId ??
            analysis.operationsWithValues?.[0]?.labSampleAnalysisId;
          if (labSampleAnalysisId == null || byId.has(labSampleAnalysisId)) {
            return;
          }
          byId.set(labSampleAnalysisId, {
            value: labSampleAnalysisId,
            label: [analysis.code, analysis.description]
              .filter(Boolean)
              .join(' - '),
          });
        });
        return Array.from(byId.values());
      },
      modalTitle() {
        const code = this.sample.labSampleCode ?? this.sample.labSampleCode;
        return `${this.$t('advancedRevision.wizard.title')} - ${code}`;
      },
      isPostSignature() {
        if (this.mode) return this.mode === 'postSignature';
        const status =
          this.labSample?.sampleReportStatus ?? this.sample.sampleReportStatus;
        return status === SAMPLE_REPORT_STATUSES.APPROVED;
      },
      errorTypes() {
        return this.isPostSignature ? SIGNED_ERROR_TYPES : UNSIGNED_ERROR_TYPES;
      },
      steps() {
        const steps = ['reason'];
        steps.push('sample');
        steps.push('analyses');
        if (this.trails.length > 0 || this.hasTemperature) {
          steps.push('temperature');
        }
        return steps;
      },
      currentStepKey() {
        return this.steps[this.stepIndex];
      },
      isLastStep() {
        return this.stepIndex === this.steps.length - 1;
      },
    },
    watch: {
      //   own        (Errore Complife)       → not billed
      //   client     (Richiesta cliente)     → billable
      //   correction (Correzione pre-firma)  → not billed (no new reprint)
      'form.errorType': function onErrorTypeChange(type) {
        const billableByType = {
          own: false,
          client: true,
          correction: false,
          other: false,
        };
        this.form.toBeInvoiced = billableByType[type] ?? false;
      },
    },
    created() {
      this.fetchLocations();
      // They're fetched up-front to size the stepper correctly. The
      // heavy analyses payload stays lazy (loaded on the analyses step).
      this.fetchTemperatureTrails();
      this.initStep();
    },
    methods: {
      async fetchLocations() {
        try {
          const { data } = await locationsManagementApi.autocompleteFetch(
            'name',
            ''
          );
          this.locations = data || [];
        } catch (error) {
          this.locations = [];
        }
      },
      async initStep() {
        if (this.currentStepKey === 'sample' && !this.labSample) {
          await this.fetchLabSample();
        }
      },
      async fetchLabSample() {
        this.loading = true;
        try {
          const labSampleId = this.sample.labSampleId ?? this.sample.id;
          const { data } = await advancedRevisionApi.getLabSample(labSampleId);
          this.labSample = data;
          this.syncFormWithLabSample();
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t('advancedRevision.wizard.loadError'),
          });
        } finally {
          this.loading = false;
        }
      },
      syncFormWithLabSample() {
        if (!this.labSample) return;
        this.form.accredited = this.labSample.accredited ?? false;
        const s = this.form.sample;
        const ls = this.labSample;
        s.sampleAnalysisStart = ls.sampleAnalysisStart ?? null;
        s.sampleAnalysisEnd = ls.sampleAnalysisEnd ?? null;
        s.acceptedAt = ls.acceptedAt ?? null;
        s.lastReportDate = ls.lastReportDate ?? null;
        s.sector = ls.sector ?? null;
        s.sampleDescription = ls.sampleDescription ?? null;
        s.packagesNumber = ls.packagesNumber ?? null;
        s.productCode = ls.productCode ?? null;
        s.productName = ls.productName ?? null;
        s.customerProductName = ls.customerProductName ?? null;
        s.lot = ls.lot ?? null;
        s.date = ls.date ?? null;
        s.finalCustomer = ls.finalCustomer ?? null;
        s.dilution = ls.dilution ?? null;
        s.notes = ls.notes ?? null;
        s.comments = ls.comments ?? null;
        s.costCenter = ls.costCenter ?? null;
        s.addressId = ls.addressId ?? null;
        s.customerAddress = ls.customerAddress ?? null;
        s.analysisNotes = ls.analysisNotes ?? null;
        s.labNotes = ls.labNotes ?? null;
        if (ls.samplingInfo) {
          s.samplingInfo = { ...ls.samplingInfo };
        }
      },
      async fetchAnalyses() {
        this.loading = true;
        try {
          const labSampleId = this.sample.labSampleId ?? this.sample.id;
          const { data } = await advancedRevisionApi.getAnalyses(labSampleId);
          this.analyses = this.prepareAnalyses(data, this.labSample);
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t('advancedRevision.wizard.loadError'),
          });
        } finally {
          this.loading = false;
        }
      },
      async fetchTemperatureTrails() {
        try {
          const { data } = await advancedRevisionApi.getTemperature(
            this.labSampleId
          );
          this.trails = data || [];
          if (this.trails.length > 0) this.hasTemperature = true;
        } catch (error) {
          this.trails = [];
        }
      },
      // Called by the temperature step after an add/delete. Reloads the trails
      // and clamps stepIndex: deleting the last trail drops the 'temperature'
      // step from `steps`, which would otherwise leave stepIndex out of range.
      async refreshTemperatureTrails() {
        // Overlay the wizard while the reconcile runs so the user has feedback
        // that the (background) refetch is in progress after an add/delete.
        this.loading = true;
        try {
          await this.fetchTemperatureTrails();
          if (this.stepIndex > this.steps.length - 1) {
            this.stepIndex = this.steps.length - 1;
          }
        } finally {
          this.loading = false;
        }
      },
      normalizeSymbolType(value) {
        const match = Object.values(SymbolTypes).find(
          (v) => v.toLowerCase() === String(value ?? '').toLowerCase()
        );
        return match ?? value ?? null;
      },
      prepareAnalyses(analyses, labSample) {
        return (analyses || []).map((analysis) => {
          const operationsWithValues = (
            analysis.operationsWithValues || []
          ).map((owv) => ({
            ...owv,
            value: {
              validValue: owv.value?.validValue ?? false,
              ...owv.value,
              // NOTE: normalize.
              symbolType: this.normalizeSymbolType(
                owv.value?.symbolType ?? owv.symbolType
              ),
            },
          }));
          const hasDilution = operationsWithValues.some(
            (owv) =>
              owv.value?.symbolType === SymbolTypes.Diluent ||
              owv.value?.symbolType === SymbolTypes.Terrain
          );
          // dilution is being moved from LabSample to each operation value
          // row, but no migration has copied the old data over yet. Until
          // then, backfill the analysis-level editor from the legacy
          // LabSample.dilution — but only when this analysis actually
          // involves a dilution (1) and a row doesn't already carry its own
          // value (2) — so that once saved, the value is stamped onto every
          // row going forward.
          const existingRowDilution = operationsWithValues.find(
            (owv) => owv.value?.dilution
          )?.value.dilution;
          const dilution = hasDilution
            ? existingRowDilution ?? labSample?.dilution ?? null
            : null;
          return {
            ...analysis,
            extra: {
              accreditedTest: analysis.accreditedTest ?? false,
              locationid: analysis.locationId ?? null,
              hasDilution,
              dilution,
              methodCode: analysis.method ?? null,
              analysisTranslation: {
                it: analysis.description ?? null,
                en: analysis.descriptionEn ?? null,
              },
            },
            operationsWithValues,
          };
        });
      },
      prevStep() {
        if (this.stepIndex > 0) this.stepIndex -= 1;
      },
      async nextStep() {
        const nextStepKey = this.steps[this.stepIndex + 1];
        if (nextStepKey === 'sample' && !this.labSample) {
          await this.fetchLabSample();
        } else if (nextStepKey === 'analyses' && this.analyses.length === 0) {
          await this.fetchAnalyses();
        }

        if (this.stepIndex < this.steps.length - 1) this.stepIndex += 1;
      },
      // errorCause (own|client) is synthesized from the single errorType field:
      // only a client request is client-caused, everything else is own.
      resolveErrorCause() {
        return this.form.errorType === 'client' ? 'client' : 'own';
      },
      buildAnalysesValues() {
        return this.analyses.map((analysis) => ({
          analysisId: analysis.id,
          methodCode: analysis.extra.methodCode,
          analysisTranslation: analysis.extra.analysisTranslation,
          accreditedTest: analysis.extra.accreditedTest,
          locationId: analysis.extra.locationid,
          values: analysis.operationsWithValues.map((owv) => {
            const v = owv.value;
            return {
              id: v.id,
              labSampleAnalysisId:
                v.labSampleAnalysisId ?? owv.labSampleAnalysisId,
              operationRegistryId: owv.operationRegistryId,
              value: v.value,
              LD: v.LD,
              LQ: v.LQ,
              U: v.U,
              recoveryFactor: v.recoveryFactor,
              symbol: v.symbol,
              symbolLot: v.symbolLot,
              symbolType: v.symbolType,
              notes: v.notes,
              measureId: v.measureId,
              specificationType: v.specificationType,
              specificationValue: v.specificationValue,
              selectionValues: v.selectionValues?.length
                ? v.selectionValues
                : null,
              decimalDigits: v.decimalDigits,
              minRange: v.minRange,
              maxRange: v.maxRange,
              isMinRangeInclusive: v.isMinRangeInclusive,
              isMaxRangeInclusive: v.isMaxRangeInclusive,
              operation: owv.operation,
              mainResult: owv.mainResult,
              showRawData: v.showRawData,
              // Stamped automatically onto every operation of the analysis —
              // there's no per-operation dilution editor. Without a
              // diluent/terrain operation the editor is hidden, so the row
              // keeps what it already has.
              dilution: analysis.extra.hasDilution
                ? analysis.extra.dilution ?? null
                : v.dilution,
            };
          }),
        }));
      },
      buildSample() {
        const s = this.form.sample;
        return {
          sector: s.sector,
          sampleDescription: s.sampleDescription,
          packagesNumber: s.packagesNumber,
          productCode: s.productCode,
          productName: s.productName,
          customerProductName: s.customerProductName,
          lot: s.lot,
          date: s.date,
          finalCustomer: s.finalCustomer,
          samplingInfo: s.samplingInfo,
          dilution: s.dilution,
          notes: s.notes,
          comments: s.comments,
          costCenter: s.costCenter,
          addressId: s.addressId,
          customerAddress: s.customerAddress,
          analysisNotes: s.analysisNotes,
          labNotes: s.labNotes,
          // Only sent if the user explicitly edited the field — these are
          // pre-filled from the live min/max of the operation values purely
          // for display, and must not silently overwrite the report's
          // analysisStartDate/EndDate override on every revision otherwise.
          sampleAnalysisStart: s.sampleAnalysisStartTouched
            ? s.sampleAnalysisStart
            : undefined,
          sampleAnalysisEnd: s.sampleAnalysisEndTouched
            ? s.sampleAnalysisEnd
            : undefined,
          acceptedAt: s.acceptedAt,
          lastReportDate: s.lastReportDate,
        };
      },
      // AdvancedRevisionDto — POST /admin/advanced-revision/{id}/default.
      buildPayload() {
        return {
          analysesValues: this.buildAnalysesValues(),
          errorType: this.form.errorType,
          errorCause: this.resolveErrorCause(),
          errorNotes: this.form.errorNotes,
          internalNotes: this.form.internalNotes,
          toBeInvoiced: this.form.toBeInvoiced,
          sample: this.buildSample(),
          temperatureTrails: this.trails.map((t) => ({
            temperatureTrailId: t.temperatureTrailId,
            temperature: t.temperature,
            startedAt: t.startedAt,
            endedAt: t.endedAt,
            thermostatName: t.thermostatName,
            thermostatId: t.thermostatId,
          })),
        };
      },
      // ValuesRevision — challenge `data` part (slimmer; no errorType /
      // internalNotes / toBeInvoiced / temperatureTrails).
      buildChallengePayload() {
        return {
          analysesValues: this.buildAnalysesValues(),
          errorCause: this.resolveErrorCause(),
          errorNotes: this.form.errorNotes,
          sample: this.buildSample(),
        };
      },
      async submit() {
        // Hard guard for post-signature: errorType, errorNotes and internalNotes
        // are required. Independent of the stepper :disabled gate so an empty
        // reason can never be POSTed even if a step ever slips through.
        if (
          this.isPostSignature &&
          (!this.form.errorType ||
            !this.form.errorNotes ||
            !this.form.internalNotes)
        ) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: this.$t('advancedRevision.wizard.requiredReason'),
          });
          return;
        }

        this.submitting = true;
        try {
          const labSampleId =
            this.labSample.labSampleId ??
            this.sample.labSampleId ??
            this.sample.id;

          // create address if needed (mirrors CreateRevision.vue / EditSample.vue)
          this.form.sample = await analysisStatusManagementHelper.createAddress(
            this.form.sample,
            this.labSample.customerId
          );
          await analysisStatusManagementHelper.updateAddressCountryFields(
            this.form.sample
          );
          if (
            !this.form.sample.customerHeaderData &&
            this.form.sample.addressId === null
          ) {
            this.form.sample.addressId = null;
          }

          if (this.analysisType === SampleReportType.Challenge) {
            this.$router.push({
              name: 'uploadDocument',
              params: {
                labSampleId,
                revisionData: this.buildChallengePayload(),
                isAdvancedRevision: true,
              },
            });
            this.$emit('dialogClose', { success: true });
            return;
          }

          await advancedRevisionApi.createDefaultRevision(
            labSampleId,
            this.buildPayload()
          );
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('advancedRevision.wizard.success'),
          });
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message:
              error?.response?.status < 500
                ? error.response.data?.error?.message ??
                  this.$t('advancedRevision.wizard.submitError')
                : this.$t('global.serverError'),
          });
        } finally {
          this.submitting = false;
        }
      },
      handleClose() {
        this.$emit('dialogClose', { success: false });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .el-switch__label {
    color: inherit;
  }
  .el-switch.is-checked .el-switch__core {
    border-color: $--color-primary;
    background-color: $--color-primary;
  }
</style>
